<!--
  ═══════════════════════════════════════════════════════════════════════════
  COMPONENTE GENÉRICO - FORMULARIO DE ATRIBUTOS DINÁMICOS
  ═══════════════════════════════════════════════════════════════════════════

  Componente principal que renderiza campos dinámicos basados en configuración
  de atributos. Maneja validaciones, visibilidad y layout.

  Props:
  - definiciones: Array de definiciones de atributos
  - modelValue: Objeto con valores actuales (AtributosJSON parseado)
  - readonly: Si los campos son solo lectura
  - layout: 'grid' | 'vertical'
  - columns: Número de columnas para layout grid

  Emits:
  - update:modelValue: Cuando cambian los valores
  - validation-error: Cuando hay errores de validación
  - change: Cuando cambia cualquier atributo

  @author Sistema SISPRO ERP
  @version 1.0.0
-->

<template>
  <div class="atributos-dinamicos-form">
    <!-- Sin atributos -->
    <el-empty
      v-if="!definicionesVisibles || definicionesVisibles.length === 0"
      description="No hay atributos configurados"
      :image-size="80"
    />

    <!-- Renderizado en grid -->
    <el-row
      v-else-if="layout === 'grid'"
      :gutter="20"
    >
      <el-col
        v-for="def in definicionesVisibles"
        :key="def.Clave"
        :span="24 / columns"
      >
        <AtributoField
          :definicion="def"
          :modelValue="atributosLocal[def.Clave]"
          :readonly="def.Editable === false"
          @update:modelValue="handleAtributoChange(def.Clave, $event)"
          @validation-error="handleFieldValidationError(def.Clave, $event)"
        />
      </el-col>
    </el-row>

    <!-- Renderizado vertical -->
    <div
      v-else
      class="atributos-vertical"
    >
      <AtributoField
        v-for="def in definicionesVisibles"
        :key="def.Clave"
        :definicion="def"
        :modelValue="atributosLocal[def.Clave]"
        :readonly="def.Editable === false"
        @update:modelValue="handleAtributoChange(def.Clave, $event)"
        @validation-error="handleFieldValidationError(def.Clave, $event)"
      />
    </div>

    <!-- Errores de validación (solo en desarrollo) -->
    <div
      v-if="showValidationErrors && erroresValidacion.length > 0"
      class="validation-errors"
    >
      <el-alert
        v-for="(error, idx) in erroresValidacion"
        :key="idx"
        type="error"
        :title="error.campo"
        :description="error.mensaje"
        :closable="false"
        show-icon
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';
import AtributoField from './AtributoField.vue';

// ══════════════════════════════════════════════════════════════════════════
// PROPS & EMITS
// ══════════════════════════════════════════════════════════════════════════

const props = defineProps({
  // Definiciones de atributos desde backend
  definiciones: {
    type: Array,
    required: true,
    validator: (arr) => Array.isArray(arr),
  },
  // Valores actuales (objeto plano, no JSON string)
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  // Solo lectura
  readonly: {
    type: Boolean,
    default: false,
  },
  // Layout: 'grid' o 'vertical'
  layout: {
    type: String,
    default: 'grid',
    validator: (val) => ['grid', 'vertical'].includes(val),
  },
  // Número de columnas para grid
  columns: {
    type: Number,
    default: 2,
    validator: (val) => val >= 1 && val <= 4,
  },
  // Mostrar errores de validación inline
  showValidationErrors: {
    type: Boolean,
    default: false,
  },
  // Criterios de visibilidad opcionales
  criteriosVisibilidad: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue', 'validation-error', 'change']);

// ══════════════════════════════════════════════════════════════════════════
// ESTADO LOCAL
// ══════════════════════════════════════════════════════════════════════════

const atributosLocal = ref({ ...props.modelValue });
const erroresValidacion = ref([]);
const camposConError = ref(new Set());

// ══════════════════════════════════════════════════════════════════════════
// COMPUTED
// ══════════════════════════════════════════════════════════════════════════

/**
 * Filtra definiciones que deben mostrarse según criterios de visibilidad
 */
const definicionesVisibles = computed(() => {
  if (!props.definiciones || props.definiciones.length === 0) {
    return [];
  }

  // Si no hay criterios, mostrar todos los activos
  if (!props.criteriosVisibilidad || Object.keys(props.criteriosVisibilidad).length === 0) {
    return props.definiciones.filter((def) => def.Activo !== false);
  }

  // Aplicar criterios de visibilidad
  return props.definiciones.filter((def) => {
    if (def.Activo === false) return false;

    // TODO: Implementar lógica de visibilidad por módulo, ElementoTipo, MaterialCategoria
    // Por ahora retornar todos los activos
    return true;
  });
});

/**
 * Verifica si el formulario es válido
 */
const isValid = computed(() => {
  return camposConError.value.size === 0;
});

// ══════════════════════════════════════════════════════════════════════════
// MÉTODOS
// ══════════════════════════════════════════════════════════════════════════

/**
 * Maneja cambio en un atributo específico
 */
function handleAtributoChange(clave, valor) {
  atributosLocal.value[clave] = valor;

  // Emitir actualización completa
  emit('update:modelValue', { ...atributosLocal.value });
  emit('change', { clave, valor, atributos: atributosLocal.value });
}

/**
 * Maneja error de validación en un campo
 */
function handleFieldValidationError(clave, error) {
  if (error) {
    camposConError.value.add(clave);
    const errorExistente = erroresValidacion.value.findIndex((e) => e.campo === clave);
    if (errorExistente >= 0) {
      erroresValidacion.value[errorExistente] = { campo: clave, mensaje: error };
    } else {
      erroresValidacion.value.push({ campo: clave, mensaje: error });
    }
  } else {
    camposConError.value.delete(clave);
    erroresValidacion.value = erroresValidacion.value.filter((e) => e.campo !== clave);
  }

  emit('validation-error', erroresValidacion.value);
}

/**
 * Valida todos los campos
 * @returns {Promise<boolean>} true si todos los campos son válidos
 */
async function validate() {
  // Limpiar errores previos
  erroresValidacion.value = [];
  camposConError.value.clear();

  // Validar cada definición
  for (const def of definicionesVisibles.value) {
    const valor = atributosLocal.value[def.Clave];

    // Validación de requerido
    if (def.Requerido && (valor === null || valor === undefined || valor === '')) {
      const error = `El campo "${def.Etiqueta}" es requerido`;
      handleFieldValidationError(def.Clave, error);
    }

    // Validación por tipo
    if (valor !== null && valor !== undefined && valor !== '') {
      const errorTipo = validarPorTipo(def, valor);
      if (errorTipo) {
        handleFieldValidationError(def.Clave, errorTipo);
      }
    }
  }

  return isValid.value;
}

/**
 * Valida valor según tipo de atributo
 */
function validarPorTipo(definicion, valor) {
  switch (definicion.Tipo) {
    case 'number':
      if (isNaN(Number(valor))) {
        return `"${definicion.Etiqueta}" debe ser un número válido`;
      }
      break;
    case 'email': {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(valor)) {
        return `"${definicion.Etiqueta}" debe ser un email válido`;
      }
      break;
    }
    case 'url':
      try {
        new URL(valor);
      } catch {
        return `"${definicion.Etiqueta}" debe ser una URL válida`;
      }
      break;
  }
  return null;
}

/**
 * Resetea el formulario a valores iniciales
 */
function reset() {
  atributosLocal.value = {};
  erroresValidacion.value = [];
  camposConError.value.clear();
  emit('update:modelValue', {});
}

/**
 * Inicializa valores por defecto
 */
function initializeDefaults() {
  const valoresConDefecto = {};

  definicionesVisibles.value.forEach((def) => {
    if (def.ValorPorDefecto !== undefined && def.ValorPorDefecto !== null) {
      valoresConDefecto[def.Clave] = def.ValorPorDefecto;
    }
  });

  if (Object.keys(valoresConDefecto).length > 0) {
    atributosLocal.value = { ...valoresConDefecto, ...atributosLocal.value };
    emit('update:modelValue', { ...atributosLocal.value });
  }
}

// ══════════════════════════════════════════════════════════════════════════
// LIFECYCLE
// ══════════════════════════════════════════════════════════════════════════

onMounted(() => {
  // Inicializar valores por defecto si no hay valores
  if (!props.modelValue || Object.keys(props.modelValue).length === 0) {
    initializeDefaults();
  }
});

// Watch para sincronizar con cambios externos
watch(
  () => props.modelValue,
  (newVal) => {
    if (JSON.stringify(newVal) !== JSON.stringify(atributosLocal.value)) {
      atributosLocal.value = { ...newVal };
    }
  },
  { deep: true },
);

// ══════════════════════════════════════════════════════════════════════════
// EXPOSE PARA ACCESO DESDE PARENT
// ══════════════════════════════════════════════════════════════════════════

defineExpose({
  validate,
  reset,
  isValid,
  atributos: atributosLocal,
});
</script>

<style scoped>
.atributos-dinamicos-form {
  width: 100%;
}

.atributos-vertical {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.validation-errors {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
